import React, { useEffect, useRef, useState } from "react";
import { FaQuestionCircle, FaTimes, FaEnvelope } from "react-icons/fa";

// ⚠️ Placeholder — confirm the real support address before shipping.
const SUPPORT_EMAIL = "support@rajapi-cop.org";

const FAQ_ITEMS: { question: string; answer: string }[] = [
  {
    question: "Je n'ai pas reçu mon email de vérification",
    answer:
      "Vérifiez votre dossier spam. Vous pouvez aussi demander un nouvel email depuis l'écran de connexion en cliquant sur \"Renvoyer l'email de vérification\".",
  },
  {
    question: "J'ai oublié mon mot de passe",
    answer:
      'Cliquez sur "Mot de passe oublié ?" depuis la page de connexion, puis suivez les instructions envoyées par email.',
  },
  {
    question: "Je ne reçois pas mon code de vérification (2FA)",
    answer:
      'Vérifiez votre boîte de réception et vos spams. Vous pouvez demander un nouveau code via le bouton "Renvoyer le code" affiché lors de la connexion.',
  },
  {
    question: "Mon compte semble bloqué ou suspendu",
    answer:
      "Contactez le support à l'adresse ci-dessous en précisant votre nom d'utilisateur ou votre email de compte.",
  },
];

const SupportModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const closeModal = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // ⚠️ No backend "assistance form" endpoint exists yet — this opens the
    // user's email client pre-filled instead of submitting anywhere.
    // Replace with a real API call once a support-ticket endpoint exists.
    const subject = encodeURIComponent(
      `Support RAJAPI-COP — ${name || "Utilisateur"}`,
    );
    const body = encodeURIComponent(
      `Nom: ${name}\nEmail: ${email}\n\nMessage:\n${message}`,
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setIsOpen(true)}
        className="flex items-center justify-center gap-1 text-sm text-gray-500 hover:text-blue-600 mx-auto"
      >
        <FaQuestionCircle />
        Besoin d'aide ?
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="support-modal-title"
        >
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[85vh] overflow-y-auto p-6 relative">
            <button
              type="button"
              ref={closeButtonRef}
              onClick={closeModal}
              aria-label="Fermer"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <FaTimes aria-hidden="true" />
            </button>

            <h2 id="support-modal-title" className="text-lg font-semibold mb-4">
              Aide &amp; Support
            </h2>

            <div className="space-y-3 mb-6">
              {FAQ_ITEMS.map((item, i) => (
                <details
                  key={i}
                  className="border border-gray-200 rounded-lg p-3"
                >
                  <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                    {item.question}
                  </summary>
                  <p className="text-sm text-gray-600 mt-2">{item.answer}</p>
                </details>
              ))}
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-700 mb-4">
              <FaEnvelope className="text-gray-400" />
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-blue-500 hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
            </div>

            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Formulaire d'assistance
            </h3>
            <form onSubmit={handleContactSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Votre nom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Votre email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <textarea
                placeholder="Décrivez votre problème"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg text-sm"
              >
                Envoyer
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportModal;
